import React, { Component, Fragment } from 'react';
import Dropzone from 'react-dropzone';
import { produce } from 'immer';
import { FormattedMessage, injectIntl } from 'react-intl';
import { gql, graphql, compose } from 'react-apollo';

import RaisedButton from 'material-ui/RaisedButton';
import FlatButton from 'material-ui/FlatButton';
import LinearProgress from 'material-ui/LinearProgress';
import AddIcon from 'material-ui/svg-icons/content/add-circle';
import HelpIcon from 'material-ui/svg-icons/action/help-outline';
import Dialog from 'material-ui/Dialog';
import FloatingActionButton from 'material-ui/FloatingActionButton';

import sortParticipants from '../../utils/sortParticipants';
import { validateFilename, parseFileName } from '../../utils/parseFileName';
import promiseMapSeries from '../../utils/promiseMapSeries';
import fileToBase64 from '../../utils/fileToBase64';
import getGender from '../../utils/getGender';

import Paper from '../Paper';
import ParticipantForm from '../ParticipantForm';
import PaperForm from '../PaperForm';
import HelpDialogContent from '../HelpDialogContent';

import styles from './RegisterParticipants.css';

const createUploadedRegListPaperMutation = gql`
    mutation CreateUploadedRegListPaperMutation($regListId: ID!, $file: String!, $filename: String!) {
        createUploadedRegListPaper(regListId: $regListId, file: $file, filename: $filename) {
            filename
            file
            pages
        }
    }
`;

const updateRegListParticipantsMutation = gql`
    mutation UpdateRegListParticipantsMutation($regListId: ID!, $participants: [ParticipantDraftInput!]!) {
        updateRegListParticipants(regListId: $regListId, participants: $participants) {
            name
            gender
            grade
            school
            email
            paperDrafts {
                round
                level
                filename
                file
                pages
            }
        }
    }
`;

const updateParticipant = produce(({ participants }, index, field, value) => {
    participants[index][field] = value;
});

const updateParticipantPaper = produce(({ participants }, index, paperIndex, field, value) => {
    participants[index].papers[paperIndex][field] = value;
});

const updateLoosePaper = produce(({ loosePapers }, index, field, value) => {
    loosePapers[index][field] = value;
});

const insertParticipant = produce(({ participants }, participant, paper) => {
    const existingIndex = participants.findIndex(
        ({ name, grade }) => participant.name === name && participant.grade === grade
    );

    if (existingIndex > -1) {
        participants[existingIndex].papers.push(paper);
    } else {
        participants.push({ ...participant, papers: [paper] });
        sortParticipants(participants);
    }
});

const sortStateParticipants = produce(({ participants }) => {
    sortParticipants(participants);
});

const dropPaper = produce(({ participants, loosePapers }, toIndex, fromIndex, paper) => {
    if (fromIndex || fromIndex === 0) {
        participants[fromIndex].papers.splice(
            participants[fromIndex].papers.findIndex(({ file }) => file === paper.file),
            1
        );
    } else {
        loosePapers.splice(loosePapers.findIndex(({ file }) => file === paper.file), 1);
    }

    participants[toIndex].papers.push(paper);
});

const addLoosePaper = produce(({ loosePapers }, paper) => {
    loosePapers.push(paper);
});

const deleteParticipant = produce(({ participants }, index) => {
    participants.splice(index, 1);
});

const deleteParticipantPaper = produce(({ participants }, index, paperIndex) => {
    participants[index].papers.splice(paperIndex, 1);
});

const deleteLoosePaper = produce(({ loosePapers }, index) => {
    loosePapers.splice(index, 1);
});

class RegisterParticipants extends Component {
    constructor(props) {
        super(props);

        this.state = {
            participants: props.participants
                ? props.participants.map(({ email, school, gender, name, grade, paperDrafts }) => ({
                      email,
                      school,
                      gender,
                      name,
                      grade,
                      papers: paperDrafts.map(({ level, file, filename, round, pages }) => ({
                          level,
                          file,
                          filename,
                          round,
                          pages,
                      })),
                  }))
                : [],
            uploading: false,
            loosePapers: [],
            helpDialogOpen: !localStorage.getItem('viewedHelp'),
        };
    }

    handleFilesDrop = async files => {
        const {
            townName,
            season,
            regListId,
            intl: { formatMessage },
        } = this.props;

        let leftToUpload = files.length;
        this.setState({ uploading: true, overallToUpload: leftToUpload, leftToUpload });

        const uploadedPapers = await promiseMapSeries(files, async file => {
            const content = await fileToBase64(file);

            const {
                data: { createUploadedRegListPaper: paper },
            } = await this.props.createUploadedRegListPaper({
                variables: {
                    regListId: regListId,
                    file: content,
                    filename: file.name,
                },
            });

            this.setState({ leftToUpload: --leftToUpload });

            delete paper.__typename;

            return paper;
        });

        uploadedPapers.forEach(paper => {
            if (validateFilename(townName, paper.filename, season)) {
                const { name, grade, level, round } = parseFileName(townName, paper.filename, season);

                let gender = getGender(name);

                if (gender) {
                    gender = formatMessage({ id: 'EnterData.gender.' + gender });
                }

                this.setState(insertParticipant(this.state, { name, grade, gender }, { level, round, ...paper }));
            } else {
                this.setState(addLoosePaper(this.state, paper));
            }
        });

        this.setState({ uploading: false });
    };

    handleParticipantChange = (index, field, value) => {
        this.setState(updateParticipant(this.state, index, field, value));
    };

    handlePaperDrop = (toIndex, fromIndex, paper) => {
        this.setState(dropPaper(this.state, toIndex, fromIndex, paper));
    };

    handleLoosePaperChange = (index, field, value) => {
        this.setState(updateLoosePaper(this.state, index, field, value));
    };

    handleParticipantPaperChange = (index, paperIndex, field, value) => {
        this.setState(updateParticipantPaper(this.state, index, paperIndex, field, value));
    };

    handleParticipantFormFocusOut = () => {
        this.setState(sortStateParticipants(this.state));
    };

    handleParticipantDelete = index => {
        this.setState(deleteParticipant(this.state, index));
    };

    handleParticipantPaperDelete = (index, paperIndex) => {
        this.setState(deleteParticipantPaper(this.state, index, paperIndex));
    };

    handleLoosePaperDelete = index => {
        this.setState(deleteLoosePaper(this.state, index));
    };

    handleOpenLoosePaperClick = index => {
        this.setState({ selectedPaper: this.state.loosePapers[index] });
    };

    handleOpenPaperClick = (index, paperIndex) => {
        this.setState({ selectedPaper: this.state.participants[index].papers[paperIndex] });
    };

    handlePaperCloseClick = () => {
        this.setState({ selectedPaper: null });
    };

    handleAddIconClick = () => {
        this.setState({
            participants: [
                {
                    papers: [],
                    name: '',
                    school: '',
                    gender: '',
                    grade: '',
                    email: '',
                },
                ...this.state.participants,
            ],
        });
    };

    handleSaveClick = async () => {
        const { updateRegListParticipants, regListId, onSave } = this.props;

        await updateRegListParticipants({
            variables: {
                regListId,
                participants: this.state.participants.map(({ name, school, gender, grade, email, papers }) => {
                    return { name, school, gender, grade, email, paperDrafts: papers };
                }),
            },
        });

        onSave();
    };

    handleHelpClose = () => {
        this.setState({ helpDialogOpen: false });
        localStorage.setItem('viewedHelp', true);
    };

    handleHelpOpen = () => {
        this.setState({ helpDialogOpen: true });
    };

    valid = () => {
        const { participants } = this.state;

        if (!participants.length) {
            return false;
        }

        for (let i = 0; i < participants.length; i++) {
            if (
                !participants[i].name ||
                !participants[i].grade ||
                !participants[i].school ||
                !participants[i].papers.length
            ) {
                return false;
            }

            for (let j = 0; j < participants[i].papers.length; j++) {
                if (!participants[i].papers[j].round || !participants[i].papers[j].level) {
                    return false;
                }
            }
        }

        return true;
    };

    render() {
        const {
            intl: { formatMessage },
            editable,
            season,
        } = this.props;
        const {
            participants,
            loosePapers,
            uploading,
            overallToUpload,
            leftToUpload,
            selectedPaper,
            helpDialogOpen,
        } = this.state;

        return (
            <Fragment>
                {editable && (
                    <Fragment>
                        <Dropzone
                            accept={[
                                'application/pdf',
                                'application/zip',
                                'multipart/x-zip',
                                'application/octet-stream',
                                'application/zip-compressed',
                                'application/x-zip-compressed',
                            ].join(',')}
                            onDrop={this.handleFilesDrop}
                            className={styles.dropzone}
                            acceptClassName={styles.activeDropzone}
                            rejectClassName={styles.rejectDropzone}
                        >
                            <p>
                                <FormattedMessage id="UploadPapers.Drop here" />
                                <br />
                                <FormattedMessage id="UploadPapers.File types" />
                            </p>
                        </Dropzone>
                        <br />
                    </Fragment>
                )}
                {uploading && (
                    <Fragment>
                        <LinearProgress
                            mode="determinate"
                            value={((overallToUpload - leftToUpload) / overallToUpload) * 100}
                        />
                        <br />
                    </Fragment>
                )}
                {loosePapers.map((paper, index) => (
                    <PaperForm
                        paper={paper}
                        index={index}
                        onPaperChange={this.handleLoosePaperChange}
                        key={paper.file}
                        onDelete={this.handleLoosePaperDelete}
                        onOpenClick={this.handleOpenLoosePaperClick}
                        editable={true}
                        season={season}
                    />
                ))}
                {editable && (
                    <Fragment>
                        <br />
                        <br />
                        <FlatButton
                            onClick={this.handleAddIconClick}
                            label={formatMessage({ id: 'RegisterParticipants.add' })}
                            primary={true}
                            icon={<AddIcon />}
                        />
                        {!participants.length && <br />}
                    </Fragment>
                )}
                {participants.map((participant, index) => (
                    <ParticipantForm
                        key={index + '__' + participant.papers.map(({ file }) => file).join('__')}
                        index={index}
                        participant={participant}
                        onParticipantChange={this.handleParticipantChange}
                        onParticipantPaperChange={this.handleParticipantPaperChange}
                        onFocusOut={this.handleParticipantFormFocusOut}
                        onPaperDrop={this.handlePaperDrop}
                        onParticipantDelete={this.handleParticipantDelete}
                        onParticipantPaperDelete={this.handleParticipantPaperDelete}
                        onPaperOpenClick={this.handleOpenPaperClick}
                        editable={editable}
                        season={season}
                    />
                ))}
                {editable && (
                    <Fragment>
                        <RaisedButton
                            action="submit"
                            primary
                            label={formatMessage({ id: 'RegList.save' })}
                            className={styles.button}
                            onClick={this.handleSaveClick}
                            disabled={!this.valid()}
                        />
                        <FloatingActionButton
                            mini={true}
                            zDepth={0}
                            className={styles.helpButton}
                            onClick={this.handleHelpOpen}
                        >
                            <HelpIcon />
                        </FloatingActionButton>
                    </Fragment>
                )}
                {selectedPaper && (
                    <Paper
                        {...selectedPaper}
                        onCloseClick={this.handlePaperCloseClick}
                        showMarks={false}
                        editable={false}
                    />
                )}
                <Dialog
                    open={helpDialogOpen}
                    autoScrollBodyContent={true}
                    style={{ paddingTop: '1px' }}
                    repositionOnUpdate={false}
                    onRequestClose={this.handleHelpClose}
                    actions={[
                        <FlatButton
                            label={formatMessage({ id: 'Help.close' })}
                            primary={true}
                            onClick={this.handleHelpClose}
                        />,
                    ]}
                >
                    <HelpDialogContent />
                </Dialog>
            </Fragment>
        );
    }
}

export default compose(
    graphql(createUploadedRegListPaperMutation, { name: 'createUploadedRegListPaper' }),
    graphql(updateRegListParticipantsMutation, { name: 'updateRegListParticipants' }),
    injectIntl
)(RegisterParticipants);



// WEBPACK FOOTER //
// ./src/Components/RegisterParticipants/RegisterParticipants.js